import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { ReactNode } from 'react';

interface SEOProps {
	title?: string;
	description?: string;
	keywords?: string;
	ogImage?: string;
	ogType?: string;
	noindex?: boolean;
	canonicalUrl?: string;
	children?: ReactNode;
}

export const SEO = ({
	title,
	description,
	keywords,
	ogImage = 'https://cardsimp.com/__l5e/assets-v1/dc3b55df-3764-4c63-850a-afcf6fd6f651/cardsimp-og-v2.png',
	ogType = 'website',
	noindex = false,
	canonicalUrl,
	children
}: SEOProps) => {
	const location = useLocation();
	const baseUrl = 'https://cardsimp.com';

	// Use provided canonical URL or construct from current location
	const canonical = canonicalUrl || `${baseUrl}${location.pathname}`;

	// Default values
	const defaultTitle = 'CardSimp — TCG Singles, Slabs & Sealed';
	const defaultDescription =
		'Shop Pokémon + More TCG singles, graded slabs, and sealed products from CardSimp. Fast US shipping.';
	const defaultKeywords =
		'trading cards, TCG, card seller tools, inventory management, card show vendor, QR storefront, Pokemon, MTG, Yu-Gi-Oh, sports cards';

	const finalTitle = title || defaultTitle;
	const finalDescription = description || defaultDescription;
	const finalKeywords = keywords || defaultKeywords;

	return (
		<Helmet>
			{/* Basic Meta Tags */}
			<title>{finalTitle}</title>
			<meta name="description" content={finalDescription} />
			<meta name="keywords" content={finalKeywords} />
			{noindex && <meta name="robots" content="noindex,nofollow" />}

			{/* Canonical URL */}
			<link rel="canonical" href={canonical} />

			{/* Open Graph */}
			<meta property="og:title" content={finalTitle} />
			<meta property="og:description" content={finalDescription} />
			<meta property="og:type" content={ogType} />
			<meta property="og:url" content={canonical} />
			<meta property="og:image" content={ogImage} />
			<meta property="og:site_name" content="CardSimp" />

			{/* Twitter Card */}
			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:title" content={finalTitle} />
			<meta name="twitter:description" content={finalDescription} />
			<meta name="twitter:image" content={ogImage} />

			{/* Additional SEO */}
			<meta name="author" content="CardSimp" />

			{/* Allow custom children (e.g., JSON-LD structured data) */}
			{children}
		</Helmet>
	);
};
